import { PaginationProps } from "./settings";
import { z } from "zod";
import {
  EnhancedRoleDetail,
  StructuredPermissions,
  RoleLevel,
  LeadScope,
  AccessLevel
} from "./EnhancedRoleTypes";

export const RoleFormSchema = z.object({
  userName: z.string().min(1, "Name is required"),
  userEmail: z.string().email("Invalid email address"),
  userPhone: z.string().min(1, "Phone is required"),
  role_uuid: z.string().min(1, "Role is required"),
  leadNotifications: z.array(z.string()).default([]),
  notificationsPaused: z.enum(["yes", "no"]).default("no"),
  dailyEmailReports: z.array(z.string()).default([]),
});

export type RoleFormData = z.infer<typeof RoleFormSchema>;

export interface RolesApiData {
  pagination: PaginationProps;
  roles?: RoleDetail[];
}

interface Role {
  id: string | number;
  name: string;
}

export interface RoleApiResponse {
  status: string;
  message: string;
  data: RoleDetail[];
  roles: Role[];
}

// Legacy interface for backwards compatibility
export interface RoleDetail {
  uuid: string;
  role_name: string;
  role_permissions: {
    add_leads: boolean;
    view_leads: boolean;
    edit_leads: boolean;
    delete_leads: boolean;
    export_leads: boolean;
    form_management: boolean;
    user_management: boolean;
    assign_leads: boolean;
    view_all_leads: boolean;
    add_contacts: boolean;
    view_contacts: boolean;
    edit_contacts: boolean;
    delete_contacts: boolean;
    trash: boolean;
    dashboard: boolean;
    reports: boolean;
  };
  role_description: string | null;
  users: Array<{
    uuid: string;
    userName: string;
  }>;
  // New enhanced fields for migration support
  role_level?: RoleLevel;
  lead_scope?: LeadScope;
  structured_permissions?: StructuredPermissions;
  is_system_role?: boolean;
  is_active?: boolean;
  pipeline_uuid?: string;
  pipeline_id_fk?: number;
}

// Enhanced API responses
export interface EnhancedRolesApiData {
  pagination: PaginationProps;
  roles?: EnhancedRoleDetail[];
}

export interface EnhancedRoleApiResponse {
  status: string;
  message: string;
  data: EnhancedRoleDetail[];
  roles: Role[];
}

export interface ApiErrorResponse {
  status: "error";
  message: {
      message: string;
      users?: { uuid: string; userName: string }[];
  };
}

// Helper functions to convert between old and new format
export function convertLegacyToEnhanced(legacy: RoleDetail): EnhancedRoleDetail {
  return {
    uuid: legacy.uuid,
    role_name: legacy.role_name,
    role_description: legacy.role_description,
    role_level: legacy.role_level || RoleLevel.SALES_REP,
    lead_scope: legacy.lead_scope || LeadScope.ASSIGNED,
    permissions: legacy.structured_permissions || convertLegacyPermissions(legacy.role_permissions),
    is_system_role: legacy.is_system_role || false,
    is_active: legacy.is_active !== undefined ? legacy.is_active : true,
    pipeline_uuid: legacy.pipeline_uuid,
    pipeline_id_fk: legacy.pipeline_id_fk,
    users_count: legacy.users?.length || 0
  };
}

function convertLegacyPermissions(permissions: RoleDetail['role_permissions']): StructuredPermissions {
  return {
    leads: {
      create: permissions.add_leads,
      view: permissions.view_all_leads ? LeadScope.ALL :
            permissions.view_leads ? LeadScope.ASSIGNED : LeadScope.NONE,
      edit: permissions.edit_leads ? AccessLevel.OWN : AccessLevel.NONE,
      delete: permissions.delete_leads ? AccessLevel.OWN : AccessLevel.NONE,
      assign: permissions.assign_leads,
      export: permissions.export_leads,
      import: false
    },
    contacts: {
      create: permissions.add_contacts,
      view: permissions.view_contacts ? AccessLevel.OWN : AccessLevel.NONE,
      edit: permissions.edit_contacts ? AccessLevel.OWN : AccessLevel.NONE,
      delete: permissions.delete_contacts ? AccessLevel.OWN : AccessLevel.NONE
    },
    reports: {
      view_personal: permissions.reports,
      view_team: false,
      view_all: false,
      export: false
    },
    administration: {
      user_management: permissions.user_management,
      role_management: false,
      form_management: permissions.form_management,
      system_settings: false
    },
    dashboard: {
      view_personal: permissions.dashboard,
      view_team: false,
      view_company: false
    }
  };
}
